import type { ReactNode } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from './cn';

/** Semantic colours for statuses. */
export type Tone = 'neutral' | 'brand' | 'info' | 'success' | 'warning' | 'danger' | 'violet' | 'pink';

const badge = cva('inline-flex h-5 shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2 text-xs font-bold leading-none', {
  variants: {
    tone: {
      neutral: 'border-line bg-muted text-ink-muted',
      brand: 'border-accent/25 bg-accent-soft text-accent',
      info: 'border-info-line bg-info-soft text-info-ink',
      success: 'border-success-line bg-success-soft text-success-ink',
      warning: 'border-warning-line bg-warning-soft text-warning-ink',
      danger: 'border-danger-line bg-danger-soft text-danger-ink',
      violet: 'border-violet-line bg-violet-soft text-violet-ink',
      pink: 'border-pink-line bg-pink-soft text-pink-ink',
    },
  },
  defaultVariants: { tone: 'neutral' },
});

const dot: Record<Tone, string> = {
  neutral: 'bg-ink-faint',
  brand: 'bg-accent',
  info: 'bg-info-ink',
  success: 'bg-success-ink',
  warning: 'bg-warning-ink',
  danger: 'bg-danger-ink',
  violet: 'bg-violet-ink',
  pink: 'bg-pink-ink',
};

/** A status pill. `dot` adds a leading dot (live states such as "In progress"). */
export function Badge({ tone = 'neutral', dot: withDot, className, children }: { tone?: Tone; dot?: boolean; className?: string; children: ReactNode }) {
  return (
    <span className={cn(badge({ tone }), className)}>
      {withDot && <span className={cn('size-1.5 rounded-full', dot[tone])} aria-hidden />}
      {children}
    </span>
  );
}

const COLOR_TONES: Record<string, Tone> = {
  green: 'success',
  success: 'success',
  cyan: 'brand',
  blue: 'info',
  geekblue: 'info',
  processing: 'info',
  gold: 'warning',
  orange: 'warning',
  warning: 'warning',
  yellow: 'warning',
  red: 'danger',
  volcano: 'danger',
  error: 'danger',
  purple: 'violet',
  magenta: 'pink',
};

/**
 * Tone for a colour name, for label maps that name a colour per code
 * (e.g. `{ label: 'Error', color: 'red' }` → danger). Unknown colours are neutral.
 */
export function toneOf(color: string | undefined): Tone {
  return (color && COLOR_TONES[color]) || 'neutral';
}
